import TextEdit from '@/components/TextEdit'
import { EmbedStore } from '@/store'
import { AtlasEmbed, Embed, PassivesEmbed } from '@/types/embed'
import Dialog from '@/ui/dialog'
import Tooltip from '@/ui/tooltip'
import {
  faChevronDown,
  faChevronUp,
  faEdit,
  faTimes
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React from 'react'

function TreeVariant({
  embed,
  onChange,
  index
}: {
  embed: PassivesEmbed | AtlasEmbed
  onChange: (value: Embed | ((prev: Embed) => Embed)) => void
  index: number
}) {
  const [editing, setEditing] = React.useState(false)

  const onEdit = React.useCallback((e: React.UIEvent) => {
    e.stopPropagation()
    setEditing((flag) => !flag)
  }, [])
  const onEditChange = React.useCallback(
    (name: string | undefined) => {
      setEditing(false)
      if (name) {
        onChange((embed) => {
          if (!('variants' in embed)) return embed
          embed = { ...embed }
          embed.variants = embed.variants.slice()
          embed.variants[index] = { ...embed.variants[index], name }
          return embed
        })
      }
    },
    [onChange, index]
  )
  const onDelete = React.useCallback(
    () =>
      onChange((embed) => {
        if (!('variants' in embed)) return embed
        if (embed.variants.length <= 1) return embed
        embed = { ...embed }
        embed.variants = embed.variants.slice()
        embed.variants.splice(index, 1)
        if (embed.active > index) {
          embed.active -= 1
        } else if (embed.active > embed.variants.length) {
          embed.active = embed.variants.length - 1
        }
        return embed
      }),
    [onChange, index]
  )

  const onMoveUp = React.useCallback(
    () =>
      onChange((embed) => {
        if (!('variants' in embed)) return embed
        if (index <= 0) return embed
        embed = { ...embed }
        embed.variants = embed.variants.slice()
        const value = embed.variants.splice(index, 1)
        embed.variants.splice(index - 1, 0, ...value)
        if (embed.active === index) {
          embed.active = index - 1
        } else if (embed.active === index - 1) {
          embed.active = index
        }
        return embed
      }),
    [onChange, index]
  )
  const onMoveDown = React.useCallback(
    () =>
      onChange((embed) => {
        if (!('variants' in embed)) return embed
        if (index >= embed.variants.length - 1) return embed
        embed = { ...embed }
        embed.variants = embed.variants.slice()
        const value = embed.variants.splice(index, 1)
        embed.variants.splice(index + 1, 0, ...value)
        if (embed.active === index) {
          embed.active = index + 1
        } else if (embed.active === index + 1) {
          embed.active = index
        }
        return embed
      }),
    [onChange, index]
  )

  const data = embed.variants[index]
  if (!data) return null

  return (
    <li className={classNames({ 'poe-editing': editing })}>
      {editing ? (
        <TextEdit value={data.name} onChange={onEditChange} />
      ) : (
        data.name
      )}
      <FontAwesomeIcon
        className={classNames('poe-delete', {
          'poe-disabled': embed.variants.length <= 1
        })}
        icon={faTimes}
        onClick={onDelete}
      />
      <FontAwesomeIcon className="poe-rename" icon={faEdit} onClick={onEdit} />
      <FontAwesomeIcon
        className={classNames('poe-move', {
          'poe-disabled': index >= embed.variants.length - 1
        })}
        icon={faChevronDown}
        onClick={onMoveDown}
      />
      <FontAwesomeIcon
        className={classNames('poe-move', { 'poe-disabled': index <= 0 })}
        icon={faChevronUp}
        onClick={onMoveUp}
      />
    </li>
  )
}

export function VariantEditor({
  embed,
  onChange,
  onClose
}: {
  embed: PassivesEmbed | AtlasEmbed
  onChange: (value: Embed | ((prev: Embed) => Embed)) => void
  onClose: () => void
}) {
  const onAddVariant = React.useCallback(
    () =>
      onChange((embed) => {
        if (!('variants' in embed)) return embed
        const variant = {
          ...(embed.variants[embed.variants.length - 1] ||
            EmbedStore.passives.variants[0])
        }
        variant.name = `Variant ${embed.variants.length + 1}`
        embed = { ...embed, variants: [...embed.variants, variant] }
        embed.active = embed.variants.length - 1
        return embed
      }),
    [onChange]
  )
  return (
    <Dialog
      className="poe-TreeVariantEditor"
      title="Edit variants"
      modal
      onClose={onClose}>
      <Dialog.Content>
        <ul className="poe-variants">
          {embed.variants.map((_, index) => (
            <TreeVariant
              key={index}
              index={index}
              embed={embed}
              onChange={onChange}
            />
          ))}
          <li onClick={onAddVariant}>Add Variant</li>
        </ul>
      </Dialog.Content>
    </Dialog>
  )
}

export function PassiveTreeVariants({
  embed,
  onChange,
  setActive
}: {
  embed: PassivesEmbed | AtlasEmbed
  onChange?: (value: Embed | ((prev: Embed) => Embed)) => void
  setActive: (index: number) => void
}) {
  const [editVariants, setEditVariants] = React.useState(false)
  return (
    <>
      {!!(onChange || embed.variants.length > 1) && (
        <div className="poe-variants">
          {embed.variants.map(({ name }, index) => (
            <div
              key={index}
              className={classNames('poe-variant', {
                'poe-active': index === embed.active
              })}
              onClick={() => setActive(index)}>
              {name}
            </div>
          ))}
          {!!onChange && (
            <Tooltip tooltip="Edit variants">
              <div className="poe-edit" onClick={() => setEditVariants(true)}>
                <FontAwesomeIcon icon={faEdit} />
              </div>
            </Tooltip>
          )}
        </div>
      )}
      {!!(editVariants && onChange) && (
        <VariantEditor
          embed={embed}
          onChange={onChange}
          onClose={() => setEditVariants(false)}
        />
      )}
    </>
  )
}
